import {
  enumQuoteSortOption,
  enumQuoteType,
  QuoteSortOption,
  QuoteStatus,
  QuoteType,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { quoteAcceptAtom } from '../_graphql/mutationQuoteAccept';
import { quoteDeleteAtom } from '../_graphql/mutationQuoteDelete';
import { quoteRejectAtom } from '../_graphql/mutationQuoteReject';
import { quoteSendAtom } from '../_graphql/mutationQuoteSend';
import { quoteVoidAtom } from '../_graphql/mutationQuoteVoid';
import { quotesListQueryAtom } from '../_graphql/queryQuotesList';
import { QuoteRecord } from '../types/quote';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $selectedAccountIds = atomFromQueryString.stringSet('accountIds');
export const $selectedQuoteType = atomFromQueryString.string<QuoteType>(
  'type',
  enumQuoteType.Sales,
);
export const $selectedStatuses =
  atomFromQueryString.stringSet<QuoteStatus>('statuses');
export const $selectedFacilityId = atomFromQueryString.string('facilityId');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isCreateDialogOpen = atom<boolean>(false);
export const $isDeleteDialogOpen = atom<boolean>(false);
export const $quotesToDelete = atom<
  Array<{ id: string; quoteNumber: string; status: QuoteStatus }>
>([]);
export const $selectedQuote = atom<QuoteRecord | null>(null);
export const $currentSorting = atom<QuoteSortOption[]>([
  enumQuoteSortOption.CreatedAtDesc,
]);

// 🔵🔵🔵 COMPUTED
export const $filtersActive = atom((get) => {
  const searchTerm = get($searchTerm);
  const statuses = get($selectedStatuses);
  const facilityId = get($selectedFacilityId);
  const accountIds = get($selectedAccountIds);
  return (
    (searchTerm !== undefined && searchTerm.trim() !== '') ||
    statuses.size > 0 ||
    !!facilityId ||
    accountIds.size > 0
  );
});

// 🟢🟢🟢 REMOTE DATA -> quotes list
export const $remoteQuotes = quotesListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const limit = get($limit);
  const offset = get($offset);
  const accountIds = Array.from(get($selectedAccountIds));
  const facilityId = get($selectedFacilityId);
  const quoteType = get($selectedQuoteType);
  const statuses = Array.from(get($selectedStatuses));
  const sorting = get($currentSorting);

  return {
    variables: {
      searchTerm: searchTerm || undefined,
      accountIds: accountIds.length > 0 ? accountIds : undefined,
      facilityIds: facilityId ? [facilityId] : undefined,
      types: quoteType ? [quoteType as QuoteType] : undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      limit,
      offset,
      sorting,
    },
  };
});

// 🟠🟠🟠 ACTION -> delete quote
export const $deleteQuote = quoteDeleteAtom({
  onSuccess: (get) => {
    get($remoteQuotes).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> send quote
export const $sendQuote = quoteSendAtom({
  onSuccess: (get) => {
    get($remoteQuotes).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> accept quote
export const $acceptQuote = quoteAcceptAtom({
  onSuccess: (get) => {
    get($remoteQuotes).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> reject quote
export const $rejectQuote = quoteRejectAtom({
  onSuccess: (get) => {
    get($remoteQuotes).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> void quote
export const $voidQuote = quoteVoidAtom({
  onSuccess: (get) => {
    get($remoteQuotes).refetch?.();
  },
});
